import { RotateCcw } from "lucide-react";
import { useState } from "react";

import { Load } from "components/load";
import PageError from "components/page-error";
import { Button } from "components/ui/button";

import { useGetDepositBatchesQuery } from "@/services/deposit-reconciliation";
import type { IDepositBatchesParams } from "src/@types/deposit-reconciliation";
import { layoutClasses } from "@/utils/css-classes";

import { useDepositBatchesFilterActions } from "./ducks/filter/hooks/actions";
import { useDepositBatchesFilterState } from "./ducks/filter/hooks/filter-state";
import BatchDetailModal from "./components/batch-detail-modal";
import KpiCards from "./components/kpi-cards";
import SelectFilters from "./components/select-filters";
import DepositBatchesTable from "./components/table";
import PeriodRangeFilter from "@/components/PeriodRangeFilter";
import { isValidPeriod } from "@/utils/date-range";

const PAGE_SIZE = 20;

export default function DepositBatchesPanel() {
  const { updateFilter } = useDepositBatchesFilterActions();
  const { filters } = useDepositBatchesFilterState();
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const pageSize: number = filters.pageSize ?? PAGE_SIZE;

  const queryParams: IDepositBatchesParams = {
    storeCode: filters.storeCode,
    periodStart: filters.periodStart,
    periodEnd: filters.periodEnd,
    committedAtFrom: filters.committedAtFrom,
    committedAtTo: filters.committedAtTo,
    status: filters.status,
    page: filters.meta?.page || 1,
    pageSize,
  };

  const periodValid: boolean = isValidPeriod(
    filters.periodStart,
    filters.periodEnd,
  );
  const committedRangeValid: boolean =
    !filters.committedAtFrom && !filters.committedAtTo
      ? true
      : isValidPeriod(filters.committedAtFrom, filters.committedAtTo);
  const filtersValid: boolean = periodValid && committedRangeValid;

  const { data, isLoading, isFetching, error, refetch } =
    useGetDepositBatchesQuery(queryParams, {
      refetchOnFocus: false,
      skip: !filtersValid,
    });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const currentPage: number = data?.page ?? queryParams.page ?? 1;
  const summary = data?.summary ?? {
    activeCount: 0,
    undoneCount: 0,
    activeAmountSum: 0,
  };

  const handlePeriodChange = (
    start: string | undefined,
    end: string | undefined,
  ) => {
    updateFilter({ key: "periodStart", value: start });
    updateFilter({ key: "periodEnd", value: end });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handlePageChange = (page: number) => {
    updateFilter({ key: "meta", value: { page } });
  };

  const handlePageSizeChange = (size: number) => {
    updateFilter({ key: "pageSize", value: size });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleViewDetails = (batchId: number) => {
    setSelectedBatchId(batchId);
  };

  if (error) return <PageError status={500} message="ERRO INTERNO NO SERVIDOR" />;
  if ((isLoading || isFetching) && rows.length === 0) return <Load />;

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-end gap-2 mb-4">
        <PeriodRangeFilter
          start={filters.periodStart}
          end={filters.periodEnd}
          onChange={handlePeriodChange}
        />
      </div>

      <KpiCards summary={summary} totalCount={total} />

      <div className={layoutClasses.contentWrapper}>
        <div className="p-0">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <SelectFilters />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 min-w-[120px] border-[#2B0C1A] text-[#2B0C1A] hover:bg-[#2B0C1A] hover:text-white"
            >
              <RotateCcw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              {isFetching ? "Carregando..." : "Recarregar"}
            </Button>
          </div>
        </div>

        <div className={layoutClasses.tableContainer}>
          <div className={layoutClasses.tableWrapper}>
            <DepositBatchesTable
              data={rows}
              page={currentPage}
              pageSize={pageSize}
              total={total}
              isFetching={isFetching}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onViewDetails={handleViewDetails}
            />
          </div>
        </div>
      </div>

      <BatchDetailModal
        batchId={selectedBatchId}
        onClose={() => setSelectedBatchId(null)}
        onChanged={() => refetch()}
      />
    </div>
  );
}
